import {
  Button,
  Box,
  Container,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  useTheme,
  Stack,
} from "@mui/material";
import { useState, useEffect, useReducer, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import GoogleButton from "react-google-button";
import HorizontalBlade, {
  StyledFeatureTypography,
} from "../../components/blades/horizontalblade.js";
import auth from "../../config/firebase.js";

const subtleBackgroundImage1 = "/background-images/reef1.jpg";
const subtleBackgroundImage2 = "/background-images/reef2.jpg";
const subtleBackgroundImage3 = "/background-images/diver1.jpg";
const subtleBackgroundImage4 = "/background-images/reef3.jpg";
const subtleBackgroundImage5 = "/background-images/reef4.jpg";

// Login-specific state reducer
const loginReducer = (state, action) => {
  switch (action.type) {
    case "SET_FORM_DATA":
      return { ...state, formData: { ...state.formData, ...action.payload } };
    case "SET_UI_STATE":
      return { ...state, uiState: { ...state.uiState, ...action.payload } };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "CLEAR_ERROR":
      return { ...state, error: null };
    case "RESET_FORM":
      return { ...initialLoginState };
    default:
      return state;
  }
};

// Initial state for login component
const initialLoginState = {
  formData: {
    email: "",
    password: "",
    termsAgreed: false,
  },
  uiState: {
    loginLoading: false,
    facebookButtonDisabled: true,
  },
  error: null,
};

// Custom hook for login logic
const useLoginLogic = () => {
  const [state, dispatch] = useReducer(loginReducer, initialLoginState);
  const navigate = useNavigate();
  const auth = useAuth();

  // Memoized action creators
  const actions = useCallback(
    {
      setFormData: (data) => dispatch({ type: "SET_FORM_DATA", payload: data }),
      setUiState: (data) => dispatch({ type: "SET_UI_STATE", payload: data }),
      setError: (error) => dispatch({ type: "SET_ERROR", payload: error }),
      clearError: () => dispatch({ type: "CLEAR_ERROR" }),
      resetForm: () => dispatch({ type: "RESET_FORM" }),
    },
    []
  );

  // Declarative effect for user verification redirect
  useEffect(() => {
    const shouldRedirect =
      auth.currentUser && auth.userVerified && !auth.loading;

    if (shouldRedirect) {
      console.log("Redirecting verified user to home");
      navigate("/");
    }
  }, [auth.currentUser, auth.userVerified, auth.loading, navigate]);

  // Declarative Google login handler
  const handleGoogleLogin = useCallback(async () => {
    if (!state.formData.termsAgreed) {
      actions.setError(
        "Please agree to the terms and conditions before proceeding."
      );
      return;
    }

    actions.setUiState({ loginLoading: true });
    actions.clearError();

    try {
      await auth.googleAuthHandler(true);
      // Navigation handled by useEffect
    } catch (error) {
      console.error("Google login failed:", error);
      const errorMessage = error.message || "Failed to login with Google";

      // Only set error if it's not an account redirect
      if (!errorMessage.includes("Account not found")) {
        actions.setError(errorMessage);
      }
    } finally {
      actions.setUiState({ loginLoading: false });
    }
  }, [state.formData.termsAgreed, actions, auth]);

  // Declarative terms agreement handler
  const handleTermsAgreement = useCallback(
    (event) => {
      const isChecked = event.target.checked;
      actions.setFormData({ termsAgreed: isChecked });

      // Clear terms-related errors
      if (state.error && state.error.includes("terms and conditions")) {
        actions.clearError();
      }
    },
    [actions, state.error]
  );

  return {
    state,
    actions,
    handlers: {
      handleGoogleLogin,
      handleTermsAgreement,
    },
    authState: auth.getAuthState(),
  };
};

// Presentational component for loading state
const LoadingView = () => (
  <Container
    maxWidth="md"
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "100vh",
    }}
  >
    <Typography>Loading...</Typography>
  </Container>
);

// Presentational component for error display
const ErrorDisplay = ({ error, theme }) => {
  if (!error) return null;

  const isInfoError = error.toString().includes("Account not found");

  return (
    <Box
      sx={{
        textAlign: "center",
        mb: 2,
        p: 2,
        backgroundColor: isInfoError
          ? theme.palette.info.light
          : theme.palette.error.light,
        borderRadius: 1,
        color: isInfoError
          ? theme.palette.info.contrastText
          : theme.palette.error.contrastText,
      }}
    >
      <Typography>{error}</Typography>
    </Box>
  );
};

// Presentational component for terms checkbox
const TermsCheckbox = ({ checked, onChange, theme }) => (
  <FormControlLabel
    control={
      <Checkbox
        sx={{
          color: theme.palette.text.primary,
          "&.Mui-checked": {
            color: theme.palette.text.primary,
          },
        }}
        checked={checked}
        onChange={onChange}
      />
    }
    label={
      <Typography sx={{ color: theme.palette.text.primary }}>
        I agree to the terms and conditions. I understand that I am responsible
        for my own actions and safety and I will not hold mydivingsites.com, its
        affiliates, or any of its members liable for any damages or injuries
        that may occur as a result of my use of this site.
      </Typography>
    }
  />
);

// Presentational component for Google login button
const GoogleLoginButton = ({
  onClick,
  disabled,
  termsAgreed,
  loginLoading,
  authLoading,
}) => {
  const isDisabled = !termsAgreed || loginLoading || authLoading;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <GoogleButton
        onClick={onClick}
        disabled={isDisabled}
        style={{
          opacity: isDisabled ? 0.6 : 1,
          margin: "0 auto",
          display: "flex",
          justifyContent: "center",
        }}
      />
    </Box>
  );
};

// Main login component - now purely declarative
export default function Login() {
  const theme = useTheme();
  const { state, handlers, authState } = useLoginLogic();

  // Show loading state while auth is being determined
  if (authState.loading) {
    return <LoadingView />;
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        py: 6,
        px: 4,
        backgroundImage: `url(${subtleBackgroundImage1})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
      }}
    >
      <HorizontalBlade
        style={{
          opacity: 0.85,
          backgroundSize: "fill",
          backgroundPosition: "center",
          padding: "24px",
          display: "flex",
          justifyContent: "center",
        }}
        sx={{
          "&:hover": {
            transform: "none !important",
            boxShadow: "none !important",
          },
        }}
      >
        <StyledFeatureTypography
          variant="h3"
          component="h1"
          sx={{ mb: 4, textAlign: "center", color: theme.palette.text.primary }}
        >
          Welcome Back! Login to your Account
        </StyledFeatureTypography>
      </HorizontalBlade>

      <HorizontalBlade
        style={{
          opacity: 0.85,
          backgroundSize: "fill",
          backgroundPosition: "center",
          padding: "24px",
          display: "flex",
          justifyContent: "center",
        }}
        sx={{
          "&:hover": {
            transform: "none !important",
            boxShadow: "none !important",
          },
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: "100%",
            maxWidth: "400px",
            margin: "0 auto",
            padding: theme.spacing(3),
          }}
        >
          <StyledFeatureTypography
            sx={{
              mb: 4,
              textAlign: "center",
              color: theme.palette.text.primary,
            }}
          >
            Login with Single Sign-On
          </StyledFeatureTypography>

          <ErrorDisplay error={state.error} theme={theme} />

          <TermsCheckbox
            checked={state.formData.termsAgreed}
            onChange={handlers.handleTermsAgreement}
            theme={theme}
          />

          <GoogleLoginButton
            onClick={handlers.handleGoogleLogin}
            termsAgreed={state.formData.termsAgreed}
            loginLoading={state.uiState.loginLoading}
            authLoading={authState.loading}
          />

          {state.uiState.loginLoading && (
            <Typography
              sx={{ textAlign: "center", color: theme.palette.text.primary }}
            >
              Logging in...
            </Typography>
          )}

          <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
            Don't have an account? <Link to="/register">Register</Link>
          </Typography>
        </Stack>
      </HorizontalBlade>
    </Container>
  );
}
